import React, {Component} from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import ReactTable from 'react-table';
import {RESOURCES as reportsResources} from '../fuseReportsResourceMap';
import {convertDate, copyObjectArray, getTodayOffsetHour} from '../shared/functions';
import {getGetHeaders} from '../shared/serverFunctions';
import {setDefaultTableSort, filterCaseInsensitive} from '../shared/tableFunctions';
import {getTimeZoneByDateAndOffset} from '../shared/dateTimeLists';
import BooleanIcon from '../buttons/BooleanIcon';
import ReportJobDetail from './ReportJobDetail';
import {ReportJobsDownloadFiles} from './ReportJobsDownloadFiles';
import TablePagination from './TablePagination';
import Config from '../messages';
import {REPORT_JOB_STATUSES} from '../shared/reportJobLists';
import {tableStyles} from '../shared/styles-table';
import CircularProgress from 'material-ui/CircularProgress';
import Dialog from 'material-ui/Dialog';
import FlatButton from 'material-ui/FlatButton';
import DetailIcon from 'material-ui/svg-icons/image/remove-red-eye';
import RefreshIcon from 'material-ui/svg-icons/navigation/refresh';

/**
 * Report Jobs class for retrieving and displaying summary
 */
class ReportJobsTable extends Component {
    constructor(props) {
        super(props);
        this.state = {
            detailDialogOpen: false,
            errorMessage: null,
            jobDetail: null,
            loading: false,
            reportCategory: '',
            reportSearch: '',
            reportSelf: false,
            reportType: '',
            summaryComplete: false,
            summaryList: [],
            summaryTableData: [],
            summaryTotal: 0,
            tableLimit: 10,
            tableOffset: 0,
            tablePage: 0,
            tableSort: {id: 'name', desc: false}
        };

        this.fetchJobsData = this.fetchJobsData.bind(this);
        this.handleRefreshJob = this.handleRefreshJob.bind(this);
        this.handlePaginationChange = this.handlePaginationChange.bind(this);
        this.handleTableChange = this.handleTableChange.bind(this);
    }

    componentWillMount() {
        this.fetchJobsData();
    }

    componentWillReceiveProps(nextProps, nextContext) {
        if (nextProps.reportSearch !== this.props.reportSearch) {
            this.setState({
                errorMessage: null,
                reportSearch: nextProps.reportSearch
            });
        }
        if (nextProps.reportSelf !== this.props.reportSelf) {
            this.setState({
                errorMessage: null,
                reportSelf: nextProps.reportSelf
            });
        }
        if (nextProps.reportCategory !== this.props.reportCategory) {
            this.setState({
                errorMessage: null,
                reportCategory: nextProps.reportCategory,
                reportType: '',
                tableOffset: 0,
                tablePage: 0
            });
        }
        if (nextProps.reportType !== this.props.reportType) {
            this.setState({
                errorMessage: null,
                reportType: nextProps.reportType,
                tableOffset: 0,
                tablePage: 0
            });
        }
    }

    componentDidUpdate(prevProps, prevState, snapshot) {
        if (
            prevProps.reportCategory !== this.props.reportCategory ||
            prevProps.reportType !== this.props.reportType ||
            prevProps.reportSearch !== this.props.reportSearch ||
            prevProps.reportSelf !== this.props.reportSelf
        ) {
            this.fetchJobsData();
        }
    }

    fetchJobsData() {
        this.setState({
            errorMessage: null,
            loading: true
        });
        const searchable = (this.state.reportSearch !== '')
            ? ['name', 'description', 'user', 'worker_class']
            : [];
        let params = {
            limit: this.state.tableLimit,
            offset: this.state.tableOffset,
            search: this.state.reportSearch,
            searchable: searchable,
            sort: this.state.tableSort
        };
        const reportCategory = this.state.reportCategory;
        const reportType = this.state.reportType;
        if (reportCategory && reportCategory !== '') {
            params.filterColumn = 'worker_package';
            params.filterValue = reportCategory;
        }
        if (reportType && reportType !== '') {
            params.filterColumn = 'worker_class';
            params.filterValue = reportType;
        }

        const reportSelf = this.state.reportSelf;
        if (reportSelf) {
            const email = localStorage.getItem('email');
            params.filterColumn = 'user';
            params.filterValue = email;
        }
        let resource = reportsResources.ReportJobs.GET_LIST(params);

        axios({
            method: 'get',
            url: resource.uri,
            headers: getGetHeaders()
        }).then((result) => {
            if (result.data && result.data.payload) {
                this.processJobsData(result.data.payload);
            }
        }).catch((error) => {
            let message = Config.fuse.error.error + 'Report Jobs.';
            if (error.response) {
                // Server response outside 2xx
                if (error.response.data && error.response.data.error) {
                    message = Config.fuse.error.error + 'Report Jobs: ' + error.response.data.error;
                }
            } else if (error.request) {
                // No response
                console.log(error.request);
            } else {
                console.log('Report Job Error: ', error.message);
            }
            this.setState({
                errorMessage: message,
                loading: false,
                summaryList: [],
                summaryTableData: [],
                summaryComplete: true,
                summaryTotal: 0
            });
        });
    }

    processJobsData = (data) => {
        let summaryList = [];
        let summaryTotal = data.total;
        const configData = JSON.parse(JSON.stringify(this.props.configData));
        for (let i = 0; i < data.rows.length; i++) {
            let job = data.rows[i];
            if (job && job.id) {
                // Get Category and Report from configuration
                let category = configData[job.worker_package];
                let report = (category && category.executors) ? category.executors[job.worker_class] : '';
                job.category = (category) ? category.title : job.worker_package;
                job.type = (report) ? report.title : (category) ? category.title : job.worker_package;

                let worker_args = job.worker_args;
                // Assemble Date Range if available
                job.range_str = 'N/A';
                if (worker_args.date_from && worker_args.date_to) {
                    job.range_str = worker_args.date_from + ' to ' + worker_args.date_to;
                }
                // Add a modified string
                job.updated_at_sort = convertDate(job.updated_at, 'query-with-time', true);
                const updated_date = new Date(job.updated_at);
                const offsetHour = getTodayOffsetHour();
                job.updated_at_str = updated_date.toLocaleString() + ' ' + getTimeZoneByDateAndOffset(updated_date, offsetHour);
                // Add Status
                const status = REPORT_JOB_STATUSES[job.state];
                job.status = (status && status.name) ? status.name : job.state;
                if (!job.user) {
                    job.user = '';
                }
                summaryList.push(job);
            }
        }

        this.setState({
            loading: false,
            summaryList: summaryList,
            summaryTableData: summaryList,
            summaryComplete: true,
            summaryTotal: summaryTotal
        });
    };

    processSingleJobData = (id, data) => {
        let summaryList = copyObjectArray(this.state.summaryList);
        if (data && id) {
            const idx = summaryList.findIndex(r => r.id === id);
            if (idx !== -1) {
                const status = REPORT_JOB_STATUSES[data.state];
                summaryList[idx].active = data.active;
                summaryList[idx].complete = data.complete;
                summaryList[idx].state_message = data.state_message;
                summaryList[idx].status = (status && status.name) ? status.name : data.state;
                summaryList[idx].updated_at = data.updated_at;
                summaryList[idx].updated_at_sort = convertDate(data.updated_at, 'query-with-time', true);
                summaryList[idx].updated_at_str = new Date(data.updated_at).toLocaleString();

                this.setState({
                    loading: false,
                    summaryList: summaryList,
                    summaryTableData: summaryList,
                    summaryComplete: true,
                    summaryTotal: summaryList.length
                });
            }
        }
    };

    handleTableChange(state, instance) {
        let offset = (state.page * state.pageSize);
        let sorted = {id: '', desc: true};
        if (state.sorted[0] && state.sorted[0].id) {
            sorted.id = state.sorted[0].id;
            sorted.desc = state.sorted[0].desc;
        } else if (state.sorted.id) {
            sorted.id = state.sorted.id;
            sorted.desc = state.sorted.desc;
        }

        this.setState({
            tableLimit: state.pageSize,
            tableOffset: offset,
            tablePage: state.page,
            tableSort: sorted
        }, () => {
            this.fetchJobsData();
        });
    }

    handlePaginationChange = (e) => {
        const state = {
            page: e.page,
            pageSize: e.pageSize,
            sorted: this.state.tableSort
        };
        this.handleTableChange(state, null);
    };

    handleRefreshJob = (e) => {
        this.processSingleJobData(e.id, e.job);
    };

    handleSummaryComplete = (isComplete) => {
        this.setState({
            summaryComplete: isComplete,
        });
    };

    openDialog = (event, type, row) => {
        if (type === 'detail') {
            this.setState({
                detailDialogOpen: true,
                jobDetail: row
            });
        }
    };

    closeDialog = (e, type) => {
        if (type === 'detail') {
            this.setState({
                detailDialogOpen: false,
                jobDetail: null
            });
        }
    };

    render() {
        const {configData} = this.props;

        const {
            detailDialogOpen,
            errorMessage,
            jobDetail,
            loading,
            summaryComplete,
            summaryList,
            summaryTotal,
            tableLimit,
            tablePage
        } = this.state;

        const defaultSort = setDefaultTableSort({field: 'updated_at', order: 'DESC'});

        const tableColumns = [
            {
                Header: 'Name',
                accessor: 'name',
                filterable: false,
                style: tableStyles.wrapText
            },
            {
                Header: 'Category',
                accessor: 'worker_package',
                filterable: false,
                Cell: (row) => <span>{(row.original.category) ? row.original.category : row.original.worker_package}</span>
            },
            {
                Header: 'Report',
                accessor: 'worker_class',
                filterable: false,
                Cell: (row) => <span>{(row.original.type) ? row.original.type : row.original.worker_class}</span>
            },
            {
                Header: 'Submitted by',
                accessor: 'user',
                filterable: false
            },
            {
                Header: 'Updated',
                accessor: 'updated_at',
                filterable: false,
                Cell: (row) => <span>{row.original.updated_at_str}</span>
            },
            {
                Header: 'Status',
                accessor: 'state',
                filterable: false,
                width: 100,
                Cell: (row) => <span>{(row.original.status) ? row.original.status : row.original.state}</span>
            },
            {
                Header: 'Message',
                accessor: 'state_message',
                filterable: false,
            },
            {
                Header: 'Complete',
                accessor: 'complete',
                Cell: row => (
                    <BooleanIcon record={row.original.complete}/>
                ),
                filterable: false,
                width: 100,
                style: tableStyles.center
            },
            {
                Header: '',
                id: 'Report-Job-Info',
                Cell: row => (
                    <DetailIcon
                        onClick={(e) => this.openDialog(e, 'detail', row.original)}
                        style={tableStyles.tableIconButton}
                    />
                ),
                filterable: false,
                sortable: false,
                width: 60
            },
            {
                Header: '',
                id: 'Report-Job-Download',
                Cell: row => {
                    const complete = row.original.complete;
                    const state = row.original.state;
                    return (
                        <ReportJobsDownloadFiles
                            disabled={(!complete && !row.original.canceled && (state === 'pending' || state === 'in_process'))}
                            id={row.original.id}
                            files={row.original.files}
                            name={row.original.name}
                            type={row.original.worker_class}
                            created={row.original.created_at}
                        />
                    )
                },
                filterable: false,
                sortable: false,
                width: 60
            }
        ];

        return (
            <div>

                {!summaryComplete && !errorMessage &&
                    <div className="loader-container">
                        <h4>Retrieving Report Jobs...</h4>
                        <CircularProgress
                            size={24}
                            thickness={2}
                            className="loader"
                        />
                    </div>
                }
                {errorMessage &&
                    <div className="error">
                        <p>{errorMessage}</p>
                    </div>
                }

                {summaryComplete &&
                    <div className="reports-table-container">

                        {/* Table Header: Report Jobs */}
                        <div className="reports-table-title table-table-header">
                            <div className="btn-report-container report-header-button-container">
                                <FlatButton
                                    label="Refresh"
                                    labelStyle={{color: "#FFFFFF"}}
                                    icon={<RefreshIcon color="#FFFFFF"/>}
                                    onClick={() => this.fetchJobsData()}
                                />
                            </div>
                            <h3 className="table-table-header-title">
                                <span>Report Jobs</span>
                            </h3>
                        </div>

                        {/* Table: Report Jobs */}
                        <ReactTable
                            key="table-report-jobs"
                            data={summaryList}
                            columns={tableColumns}
                            manual
                            defaultPageSize={10}
                            showPagination={false}
                            defaultSorted={defaultSort}
                            filterable
                            defaultFilterMethod={filterCaseInsensitive}
                            loading={loading}
                            onFetchData={(state, instance) => this.handleTableChange(state, instance)}
                            ref={(r) => this.reportJobReactTable = r}
                            resizable={false}
                            minRows={1}
                            showPageSizeOptions={true}
                            pageSizeOptions={[5, 10, 20, 50]}
                            className="-striped"
                        />
                        <TablePagination
                            page={tablePage}
                            pageSize={tableLimit}
                            pageSizeOptions={[5, 10, 20, 50]}
                            total={summaryTotal}
                            onChangeFunc={(e) => this.handlePaginationChange(e)}
                        />
                    </div>
                }
                <Dialog
                    modal={false}
                    open={detailDialogOpen}
                    onRequestClose={(e) => this.closeDialog(e, 'detail')}
                    autoScrollBodyContent={true}
                    actions={[
                        <FlatButton
                            label="Close"
                            primary={true}
                            onClick={(e) => this.closeDialog(e, 'detail')}
                        />
                    ]}
                    contentStyle={{width: '85%', maxWidth: '90%'}}
                >
                    <h3 className="dialog-title">Report Job Detail</h3>
                    <ReportJobDetail data={jobDetail} configData={configData}/>
                </Dialog>
            </div>
        )
    }
}

ReportJobsTable.propTypes = {
    configData: PropTypes.object,
    reportCategory: PropTypes.string,
    reportType: PropTypes.string,
    reportSearch: PropTypes.string,
    reportSelf: PropTypes.bool
};
ReportJobsTable.defaultProps = {
    configData: {},
    reportCategory: '',
    reportType: '',
    reportSearch: '',
    reportSelf: false
};

export default ReportJobsTable;



// WEBPACK FOOTER //
// ./src/Fuse/components/ReportJobsTable.js